<template>

    <transition name="fade">
        <div v-if="show" class="fixed inset-0 z-50 flex flex-col bg-white">

            <!-- Confirmation Modal -->
            <ConfirmationModal :show="showConfirm" title="Confirm Action"
                :message="`Are you sure you want to ${isEdit ? 'update' : 'upload'} ${files.length} file(s)?`"
                confirmText="Yes" cancelText="Cancel" :loading="loading" @confirm="handleConfirmUpload"
                @cancel="showConfirm = false" />

            <!-- Modal Header -->
            <div class="flex justify-between items-center p-4 border-b border-gray-200 bg-orange-400">
                <h2 class="text-xl font-semibold text-text-primary">
                    {{ isEdit ? 'Edit Document' : 'Upload Documents' }}
                </h2>
                <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700 text-3xl font-bold">
                    &times;
                </button>
            </div>

            <!-- Modal Body -->
            <div class="flex flex-col md:flex-row flex-1 overflow-hidden">

                <!-- Left: Dropzone -->
                <div
                    class="md:w-1/2 flex flex-col items-center justify-center p-8 border-b md:border-b-0 md:border-r border-gray-200">
                    <p class="text-sm text-text-secondary mb-4 text-center">
                        Drag & drop or click to select files (PDF, DOCX, images)
                    </p>

                    <div @click="openFileDialog" @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop" :class="[
                            'border-2 border-dashed rounded-lg p-12 flex flex-col items-center justify-center cursor-pointer transition-colors w-full max-w-md',
                            isDragging ? 'border-primary bg-primary-light-header' : 'border-gray-300 bg-white'
                        ]">
                        <div class="text-sm text-text-primary text-center">
                            <span v-if="!files.length">
                                Click or drag & drop to {{ isEdit ? 'replace' : 'upload' }}
                            </span>
                            <span v-else class="font-medium">
                                {{ files.length }} file(s) selected
                            </span>
                        </div>

                        <div class="text-xs text-gray-400 mt-2">Max 10MB each</div>

                        <input type="file" class="hidden" ref="fileInput" multiple @change="onFileChange" />
                    </div>
                </div>

                <!-- Right: File List -->
                <div class="md:w-1/2 p-8 flex flex-col flex-1">
                    <h3 class="text-lg font-semibold text-text-primary mb-3">
                        {{ isEdit ? 'File Details' : 'Files to Upload' }}
                    </h3>

                    <div class="flex-1 overflow-y-auto space-y-3">
                        <div v-for="(item, index) in files" :key="index"
                            :class="['border rounded-lg p-3 flex flex-col gap-2', index % 2 === 0 ? 'bg-white' : 'bg-gray-50']">
                            <div class="flex justify-between items-center">
                                <span class="truncate font-medium">{{ item.file?.name || item.filename }}</span>
                                <button @click="removeFile(index)" class="text-red-500 text-sm hover:underline">
                                    Remove
                                </button>
                            </div>

                            <div class="flex flex-col">
                                <label :for="'filename-' + index"
                                    class="text-sm font-medium text-gray-700 mb-1">Filename</label>
                                <input type="text" :id="'filename-' + index" v-model="item.filename"
                                    placeholder="Filename" class="w-full border rounded px-2 py-1 text-sm" />
                            </div>

                            <div class="flex flex-col">
                                <label :for="'description-' + index"
                                    class="text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                                <input type="text" :id="'description-' + index" v-model="item.description"
                                    placeholder="Optional description"
                                    class="w-full border rounded px-2 py-1 text-sm" />
                            </div>

                            <!-- NEW: Date Effective per file -->
                            <div class="flex flex-col">
                                <label :for="'date-effective-' + index"
                                    class="text-sm font-medium text-gray-700 mb-1">Effective Date</label>
                                <input type="date" :id="'date-effective-' + index" v-model="item.date_effective"
                                    class="w-full border rounded px-2 py-1 text-sm" />
                            </div>
                        </div>
                    </div>

                    <!-- Submit Button -->
                    <button :disabled="!files.length || loading || files.some(f => !f.date_effective)"
                        @click="showConfirm = true"
                        class="mt-4 w-full bg-primary-dark text-white py-3 rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors">
                        <span v-if="loading">
                            {{ isEdit ? 'Updating...' : 'Uploading...' }}
                        </span>
                        <span v-else>
                            {{ isEdit ? 'Update Document' : 'Upload & Continue' }}
                        </span>
                    </button>

                </div>
            </div>
        </div>
    </transition>

</template>

<script>
import { ref, onMounted, onBeforeUnmount, inject, watch } from "vue";
import { createDocument, updateDocument } from "@/api/documentFiles";
import ConfirmationModal from "./ConfirmationModal.vue";

export default {
    props: {
        show: Boolean,
        sectionId: Number,
        fileToEdit: Object // file object for editing
    },
    components: { ConfirmationModal },
    emits: ["close", "uploaded"],
    setup(props, { emit }) {
        const Swal = inject("$swal");
        const files = ref([]);
        const loading = ref(false);
        const isDragging = ref(false);
        const fileInput = ref(null);
        const showConfirm = ref(false);

        const isEdit = ref(false);

        // Pre-fill when editing
        watch(
            () => props.fileToEdit,
            (newFile) => {
                files.value = [];
                if (newFile) {
                    isEdit.value = true;
                    files.value.push({
                        id: newFile.id,
                        filename: newFile.filename,
                        description: newFile.description,
                        file: null,
                        date_effective: newFile.date_effective || ""
                    });
                } else {
                    isEdit.value = false;
                }
            },
            { immediate: true }
        );

        const handleEsc = (e) => {
            if ((e.key === "Escape" || e.key === "Esc") && !loading.value) {
                if (showConfirm.value) showConfirm.value = false;
                else emit("close");
            }
        };

        onMounted(() => window.addEventListener("keydown", handleEsc));
        onBeforeUnmount(() => window.removeEventListener("keydown", handleEsc));

        const openFileDialog = () => fileInput.value?.click();

        const onFileChange = (e) => {
            const newFiles = Array.from(e.target.files).map(f => ({
                file: f,
                filename: f.name,
                description: "",
                date_effective: "" // <-- add per file
            }));
            files.value = isEdit.value
                ? [{ ...files.value[0], file: newFiles[0].file, filename: newFiles[0].filename }]
                : [...files.value, ...newFiles];
            e.target.value = "";
        };

        const handleDrop = (e) => {
            isDragging.value = false;
            if (!e.dataTransfer.files.length) return;
            const newFiles = Array.from(e.dataTransfer.files).map(f => ({
                file: f,
                filename: f.name,
                description: "",
                date_effective: "" // <-- add per file
            }));
            files.value = isEdit.value
                ? [{ ...files.value[0], file: newFiles[0].file, filename: newFiles[0].filename }]
                : [...files.value, ...newFiles];
        };

        const removeFile = (index) => files.value.splice(index, 1);

        const handleConfirmUpload = async () => {
            showConfirm.value = false;
            await submitFiles();
        };

        const submitFiles = async () => {
            if (!files.value.length || !props.sectionId || files.value.some(f => !f.date_effective)) return;

            const formData = new FormData();
            formData.append("section_id", props.sectionId);

            files.value.forEach((item) => {
                // Append file
                if (item.file) {
                    formData.append(isEdit.value ? "file" : "files", item.file);
                }

                // Append metadata
                if (isEdit.value) {
                    formData.append("filename", item.filename);
                    formData.append("description", item.description || "");
                    formData.append("date_effective", item.date_effective || ""); // for single-file edit
                } else {
                    formData.append("filenames[]", item.filename);
                    formData.append("descriptions[]", item.description || "");
                    formData.append("date_effective[]", item.date_effective || ""); // NEW: per-file date
                }
            });

            loading.value = true;
            try {
                if (isEdit.value && files.value[0].id) {
                    await updateDocument(files.value[0].id, formData);
                } else {
                    await createDocument(formData);
                }

                files.value = [];
                emit("uploaded");
                emit("close");

                Swal.fire({
                    icon: "success",
                    title: isEdit.value ? "Updated!" : "Uploaded!",
                    text: `Document has been successfully ${isEdit.value ? "updated" : "uploaded"}.`,
                    timer: 2000,
                    showConfirmButton: false
                });
            } catch (err) {
                console.error(err);
                Swal.fire({
                    icon: "error",
                    title: isEdit.value ? "Update Failed" : "Upload Failed",
                    text: "There was a problem processing your request."
                });
            } finally {
                loading.value = false;
            }
        };

        return {
            files,
            loading,
            isDragging,
            fileInput,
            showConfirm,
            isEdit,
            openFileDialog,
            onFileChange,
            handleDrop,
            removeFile,
            handleConfirmUpload,
            submitFiles
        };
    }
};
</script>